import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { post } from '@youlify/auth-sdk';
import { CircleCheck, Closed, Download, File, Picture, Spinner, Trash } from '@youlify/icons';
import { RcFile } from 'antd/es/upload';
import { UploadFile, UploadFileStatus } from 'antd/es/upload/interface';
import classNames from 'classnames';
import { UploadRequestOption } from 'rc-upload/lib/interface';

import { blobDownload } from '../_util/download';
import { BASE_HTTPS_URL, HOST_ENV } from '../_util/env';
import DisabledContext from '../config-provider/DisabledContext';
import message from '../message';
import Progress from '../progress';
import Upload, { UploadProps } from '../upload';
import { useStyle } from './style/s3Upload';

export interface S3UploadChangeParams {
  permanentUrl?: string;
  preSignedUrl?: string;
  file?: UploadFile;
}

export interface S3UploadRef {
  clearUploadFile: () => void;
}

export interface S3UploadProps extends Omit<UploadProps, 'onChange'> {
  bucket?: string;
  requestBaseUrl?: string;
  uploadApiPath?: string;
  title?: string | React.ReactNode;
  accept?: string;
  value?: string;
  maxSizeMB?: number;
  readonly?: boolean;
  keepUnUploadView?: boolean;
  uploadedViewContainer?: HTMLElement | React.RefObject<HTMLElement | null> | null;
  renderUnUploadView?: () => React.ReactNode;
  renderUploadedView?: () => React.ReactNode;
  renderUploadedViewRight?: () => React.ReactNode;
  onChange?: (value?: string, changeParams?: S3UploadChangeParams) => void;
  onUploadChange?: (value?: string, changeParams?: S3UploadChangeParams) => void;
}

const formatFileSizeNumber = (size?: number) => {
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const uploadStatusMapping = {
  uploading: {
    icon: <Spinner spin={true} />,
    text: 'Uploading',
    color: '#2D40CB',
  },
  error: {
    icon: <Closed />,
    text: 'Failed.',
    color: '#A41616',
    showOperation: true,
  },
  done: {
    icon: <CircleCheck />,
    text: 'Completed',
    color: '#15803D',
  },
} as Record<
  UploadFileStatus,
  { icon: React.ReactNode; text: string; color: string; showOperation?: boolean }
>;

const classNamePrefix = 'youlify-s3-upload';

/** Fallback bucket for consumers that don't pass one. Isolated per environment:
 *  only a staging counterpart exists (there is no dev bucket), so every
 *  environment other than prod writes to `stag-`. Consumers own their own bucket
 *  names — a `bucket` prop is always passed through untouched. */
const DEFAULT_BUCKET = HOST_ENV === 'prod' ? 'youlify-upload-files' : 'stag-youlify-upload-files';

const S3Upload = forwardRef<S3UploadRef, S3UploadProps>(
  (
    {
      bucket = DEFAULT_BUCKET,
      requestBaseUrl = BASE_HTTPS_URL,
      uploadApiPath = '/api/s3/upload',
      title,
      accept,
      value,
      maxSizeMB = 10,
      readonly,
      keepUnUploadView,
      uploadedViewContainer,
      rootClassName,
      renderUnUploadView,
      renderUploadedView,
      renderUploadedViewRight,
      onChange,
      onUploadChange,
      disabled,
      ...props
    },
    ref,
  ) => {
    const contextDisabled = React.useContext(DisabledContext);
    const mergedDisabled = disabled ?? contextDisabled;
    const [messageApi, contextHolder] = message.useMessage();
    const [s3Urls, setS3Urls] = useState<{
      permanentUrl?: string;
      preSignedUrl?: string;
    }>({});
    const [uploadFile, setUploadFile] = useState<UploadFile>();
    const [showUploadDoneOperations, setShowUploadDoneOperations] = useState(false);
    const showUploadDoneOperationsTimer = useRef<ReturnType<typeof setTimeout>>(null);
    const [wrapSSR, hashId] = useStyle(classNamePrefix);
    const rootClass = classNames(classNamePrefix, hashId, rootClassName);
    const isImageType = uploadFile?.type?.startsWith('image/');
    const isUploaded = !!uploadFile;

    const clearUploadFile = () => {
      setS3Urls({});
      setShowUploadDoneOperations(false);
      setUploadFile(undefined);
      onChange?.(undefined);
      onUploadChange?.(undefined);
      showUploadDoneOperationsTimer.current && clearTimeout(showUploadDoneOperationsTimer.current);
    };

    const onBeforeUpload = (file: RcFile) => {
      const isFileTypeAllowed = accept
        ? accept?.includes(file.type || '') || accept?.includes(file.type?.split('/')?.[1])
        : true;
      const isFileSizeAllowed = file.size / 1024 / 1024 <= maxSizeMB;
      if (!isFileTypeAllowed) messageApi.error(`Only upload ${accept}`);
      if (!isFileSizeAllowed) messageApi.error(`File must be smaller than ${maxSizeMB}MB`);
      return isFileTypeAllowed && isFileSizeAllowed;
    };

    const onCustomRequest = async (options: UploadRequestOption) => {
      const file = options.file as RcFile;
      const uploadFile = {
        uid: file.uid,
        size: file.size,
        name: file.name,
        type: file.type,
        fileName: options.filename,
        status: 'uploading',
        originFileObj: file,
      } as UploadFile;
      try {
        const fileNameSplitArr = file.name.split('.');
        const extName = fileNameSplitArr.pop();
        const fileName = fileNameSplitArr.join('.');
        const objectKey = `${fileName}-${Date.now()}.${extName}`;
        const uploadAPI = `${requestBaseUrl}${uploadApiPath}`;
        const data = await post(uploadAPI, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          data: {
            bucket,
            object_key: objectKey,
            content_type: file.type || accept,
            file,
          },
          onUploadProgress: (event) => {
            const eventPercent = event.progress || 0;
            const percent = eventPercent * 100;
            uploadFile.status = 'uploading';
            if (percent < 100) {
              uploadFile.percent = percent;
              options.onProgress?.({ percent }, file);
            } else {
              uploadFile.percent = 99;
              options.onProgress?.({ percent: 99 }, file);
            }
            setUploadFile({ ...uploadFile });
          },
        });
        const permanentUrl = data?.permanent_url;
        const preSignedUrl = data?.pre_signed_url;
        if (!permanentUrl || !preSignedUrl)
          throw new Error('Upload failed, please try again later.');
        uploadFile.status = 'done';
        uploadFile.percent = 100;
        uploadFile.url = preSignedUrl;
        options.onSuccess?.(data, file);
        showUploadDoneOperationsTimer.current = setTimeout(() => {
          setShowUploadDoneOperations(true);
        }, 2000);
        setUploadFile({ ...uploadFile });
        setS3Urls({ permanentUrl, preSignedUrl });
        onChange?.(permanentUrl, { permanentUrl, preSignedUrl, file: uploadFile });
        onUploadChange?.(permanentUrl, { permanentUrl, preSignedUrl, file: uploadFile });
      } catch (error) {
        uploadFile.status = 'error';
        setUploadFile({ ...uploadFile });
        options.onError?.(error as Error);
      }
    };

    const onAuthorizationDownload = async () => {
      if (s3Urls.preSignedUrl) {
        const { name, type } = uploadFile || {};
        const downloaded = await blobDownload(s3Urls.preSignedUrl, name || 'file', type);
        if (!downloaded) messageApi.error('Download failed, please try again later.');
      }
    };

    const initValue = () => {
      const permanentUrl = value?.split('?')?.[0];
      if (permanentUrl !== s3Urls.permanentUrl) {
        const name = value?.split('/')?.pop()?.split('?')?.[0] || 'file';
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setS3Urls({ permanentUrl, preSignedUrl: value });
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setUploadFile(
          value
            ? {
                uid: '-1',
                name,
                status: 'done',
                url: value,
              }
            : undefined,
        );
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setShowUploadDoneOperations(true);
        onChange?.(permanentUrl, { permanentUrl, preSignedUrl: value });
      }
    };

    useEffect(() => {
      initValue();
    }, [value]);

    useImperativeHandle(ref, () => ({ clearUploadFile }), []);

    const renderDefaultUploadedViewMainRight = () => {
      if (!uploadFile) return null;
      const disabled = mergedDisabled;
      const { status = 'uploading' } = uploadFile;
      const statusInfo = uploadStatusMapping?.[status];
      return (
        <div className={`${classNamePrefix}-uploaded-view-main-right`}>
          {showUploadDoneOperations ? (
            disabled || readonly ? (
              <Download onClick={onAuthorizationDownload} />
            ) : (
              <div className={`${classNamePrefix}-uploaded-view-operations`}>
                <Download onClick={onAuthorizationDownload} />
                <Trash onClick={clearUploadFile} />
              </div>
            )
          ) : (
            <div className={`${classNamePrefix}-uploaded-view-status-bar`}>
              <div
                className={`${classNamePrefix}-uploaded-view-status`}
                style={{ color: statusInfo?.color }}
              >
                <span className={`${classNamePrefix}-uploaded-view-status-icon`}>
                  {statusInfo?.icon}
                </span>
                <span className={`${classNamePrefix}-uploaded-view-status-text`}>
                  {statusInfo?.text}
                </span>
              </div>
              {statusInfo?.showOperation && <Trash onClick={clearUploadFile} />}
            </div>
          )}
        </div>
      );
    };

    const renderDefaultUploadedViewProgress = () => {
      if (!uploadFile || showUploadDoneOperations) return null;
      const { status = 'uploading', percent = 0 } = uploadFile;
      const statusInfo = uploadStatusMapping?.[status];
      return (
        <div className={`${classNamePrefix}-uploaded-view-progress`}>
          <Progress
            percent={percent}
            showInfo={false}
            strokeColor={statusInfo.color}
            style={{ display: 'flex' }}
          />
        </div>
      );
    };

    const renderInnerUploadedView = () => {
      const uploadedView = renderUploadedView?.() || (
        <div
          className={classNames(`${classNamePrefix} ${hashId}`, {
            [`${classNamePrefix}-disabled`]: mergedDisabled || readonly,
          })}
          style={props.style}
        >
          <div className={`${classNamePrefix}-uploaded-view`}>
            <div className={`${classNamePrefix}-uploaded-view-main`}>
              <div className={`${classNamePrefix}-uploaded-view-main-left`}>
                <div className={`${classNamePrefix}-uploaded-view-icon`}>
                  {isImageType ? <Picture /> : <File />}
                </div>
                <div className={`${classNamePrefix}-uploaded-view-info`}>
                  <div className={`${classNamePrefix}-uploaded-view-info-name`}>
                    {uploadFile?.name || 'file'}
                  </div>
                  <div className={`${classNamePrefix}-uploaded-view-info-size`}>
                    {formatFileSizeNumber(uploadFile?.size)}
                  </div>
                </div>
              </div>
              {renderUploadedViewRight?.() || renderDefaultUploadedViewMainRight()}
            </div>
            {renderDefaultUploadedViewProgress()}
          </div>
        </div>
      );
      const container =
        uploadedViewContainer && 'current' in uploadedViewContainer
          ? uploadedViewContainer.current
          : uploadedViewContainer;
      return container ? ReactDOM.createPortal(uploadedView, container) : uploadedView;
    };

    const renderInnerUnUploadView = () => {
      const defaultCommonProps = {
        accept,
        fileList: uploadFile ? [uploadFile] : [],
        showUploadList: false,
        maxCount: 1,
        rootClassName,
        customRequest: onCustomRequest,
        beforeUpload: onBeforeUpload,
        disabled: mergedDisabled,
      };
      return renderUnUploadView ? (
        <Upload {...defaultCommonProps} {...props}>
          {renderUnUploadView()}
        </Upload>
      ) : (
        <Upload.Dragger {...defaultCommonProps} style={{ backgroundColor: '#ffffff' }} {...props}>
          <div className={`${classNamePrefix}-unupload-view`}>
            <div className={`${classNamePrefix}-unupload-view-icon`}>
              <File />
            </div>
            {title && <div className={`${classNamePrefix}-unupload-view-title`}>{title}</div>}
            <div className={`${classNamePrefix}-unupload-view-text`}>
              <span className={`${classNamePrefix}-unupload-view-text-underline`}>
                Click to upload
              </span>{' '}
              or drag and drop
            </div>
            <div className={`${classNamePrefix}-unupload-view-size-text`}>
              Max file size {maxSizeMB}MB.
            </div>
          </div>
        </Upload.Dragger>
      );
    };

    return wrapSSR(
      <div className={rootClass}>
        {contextHolder}
        {(!isUploaded || keepUnUploadView) && renderInnerUnUploadView()}
        {isUploaded && renderInnerUploadedView()}
      </div>,
    );
  },
);

export default S3Upload;
